import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useOutletContext } from "react-router-dom";
import { 
  ArrowLeft, User, Mail, ShieldCheck, 
  Lock, UserPlus, Eye, EyeOff, Loader2, Edit3, X, Trash2
} from "lucide-react";
import Button from "../components/ui/button/Button";
import axiosClient from "../api/axiosClient";
import userService from "../api/userService";
import toast, { Toaster } from 'react-hot-toast';

export default function UserDetail({ isCreateMode = false }) {
  const { id } = useParams(); 
  const navigate = useNavigate();
  const { isDark } = useOutletContext(); 

  // Use slate 300 for dark theme text and slate 600 for light theme text.
  const secondaryTextColor = isDark ? 'text-slate-300' : 'text-slate-600';

  // --- Shared Toast Styling ---
  const toastStyle = {
    style: {
      borderRadius: '12px',
      background: isDark ? '#020617' : '#ffffff', 
      color: isDark ? '#cbd5e1' : '#475569',      
      border: isDark ? '1px solid #334155' : '1px solid #e2e8f0',
      fontSize: '11px',
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      fontWeight: '800',
      padding: '12px 20px',
    },
  };

  const [isEditing, setIsEditing] = useState(isCreateMode);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const [profile, setProfile] = useState({
    full_name: "",
    role: "staff",
    email: "",
    password: "",
    is_active: 1
  });

  const roles = [
    { label: "Administrator", value: "admin" },
    { label: "Regular Staff", value: "staff" }
  ];

  useEffect(() => {
    if (!isCreateMode && id) {
      const fetchUserData = async () => {
        setLoading(true);
        try {
          const res = await axiosClient.get(`/users.php?id=${id}`);
          const userData = Array.isArray(res.data) ? res.data[0] : res.data;
          setProfile({ ...userData, password: "" });
        } catch (err) {
          toast.error("FETCH ERROR: COULD NOT LOAD USER", toastStyle);
          console.error("Fetch Error:", err);
        } finally {
          setLoading(false);
        }
      };
      fetchUserData();
    }
  }, [id, isCreateMode]);

const handleSave = async () => {
  const savePromise = isCreateMode 
    ? axiosClient.post('/users.php', profile)
    : axiosClient.put(`/users.php?id=${id}`, profile);

  toast.promise(savePromise, {
    loading: isCreateMode ? 'CREATING MEMBER...' : 'SAVING PROFILE...',
    success: () => {
      setTimeout(() => navigate('/users'), 1000);
      return isCreateMode ? 'MEMBER CREATED!' : 'PROFILE UPDATED!';
    },
    error: (err) => {
      const msg = err.response?.data?.message || "OPERATION FAILED";
      return msg.toUpperCase();
    }
  }, {
    // YOU NEED THIS THIRD ARGUMENT FOR THE THEME TO APPLY TO PROMISES
    ...toastStyle 
  });
};

  const handleDeleteUser = async () => {
    const stats = await userService.getUserStats(id);
    const taskCount = stats?.task_count || 0;

    toast((t) => (
      <div className="flex flex-col gap-3">
        <div className="space-y-1">
          <p className="font-bold text-[10px] tracking-widest text-orange-600 uppercase">
            Confirm User Purge
          </p>
          <p className="text-[11px] text-slate-500 leading-normal normal-case">
            Warning: This will also delete <span className="text-orange-600 font-bold">{taskCount} tasks</span> associated with this member.
          </p>
        </div>

        <div className="flex gap-2">
          <button 
            onClick={async () => {
              toast.dismiss(t.id);
              const loadingToast = toast.loading("PURGING USER...", toastStyle);
              try {
                await userService.purgeUser(id);
                toast.success(`PURGED USER & ${taskCount} TASKS`, { ...toastStyle, id: loadingToast });
                setTimeout(() => navigate('/users'), 1200);
              } catch (err) { 
                toast.error("PURGE FAILED", { ...toastStyle, id: loadingToast }); 
              }
            }} 
            className="bg-orange-600 text-white px-3 py-1.5 rounded-lg text-[10px] font-black uppercase shadow-lg active:scale-95"
          >
            Confirm Wipeout
          </button>
          <button 
            onClick={() => toast.dismiss(t.id)} 
            className="bg-slate-700 text-white px-3 py-1.5 rounded-lg text-[10px] font-black uppercase opacity-70 hover:opacity-100"
          >
            Cancel
          </button>
        </div>
      </div>
    ), { ...toastStyle, duration: 4000, icon: <Trash2 size={24} className="text-orange-600 shrink-0" /> });
  };

  const toggleStatus = () => {
    const newState = profile.is_active === 1 ? 0 : 1;
    setProfile({ ...profile, is_active: newState });
    toast.success(newState === 1 ? "USER STATUS: ACTIVE" : "USER STATUS: INACTIVE", toastStyle);
  };

  const panelGradient = isDark 
    ? 'bg-gradient-to-b from-slate-800 to-slate-900' 
    : 'bg-gradient-to-b from-indigo-50 to-indigo-100';

  if (loading && !isCreateMode) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="animate-spin text-indigo-500" size={40} />
      </div>
    );
  }

  return (
    <div className="p-1 sm:p-6 w-full transition-colors duration-300">
      <div className="max-w-5xl mx-auto flex flex-col">
        {/* NAVIGATION */}
        <div className="mb-8 flex justify-between items-center">
          <Button 
            variant="action"
            isDark={isDark}
            onClick={() => navigate('/users')} 
          >
            <ArrowLeft size={24} strokeWidth={3} />
          </Button>

          {!isCreateMode && (
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-3">
                <span className={`text-xs font-bold uppercase tracking-widest ${secondaryTextColor}`}>
                  {profile.is_active === 1 ? 'Active' : 'Inactive'}
                </span>
                <button
                  type="button"
                  onClick={toggleStatus}
                  disabled={!isEditing}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300 focus:outline-none shadow-inner ${
                    !isEditing ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer hover:ring-2 ring-indigo-500/20'
                  } ${profile.is_active === 1 ? 'bg-[#2d4e94c5]' : 'bg-orange-200'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-300 ${profile.is_active === 1 ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>

              <Button 
                variant="action"
                isDark={isDark}
                onClick={() => setIsEditing(!isEditing)}
                className={`!w-auto px-4 gap-2 ${isEditing ? '' : ''}`}
              >
                {isEditing ? <><X size={14} className="text-orange-500" /> <span className="text-[#2d4e94c5]'">Cancel</span></> : <><Edit3 size={14} className="text-orange-500" /> Edit Profile</>}
              </Button>
            </div>
          )}
        </div>

        {/* PROFILE CARD */}
        <div className={`w-full border rounded-[40px] overflow-hidden flex flex-col md:flex-row transition-all duration-500 shadow-xl ${
          isDark ? 'bg-slate-900 border-slate-700 shadow-black/40' : 'bg-white border-slate-200 shadow-[0_20px_50px_rgba(79,70,229,0.15)]'
        }`}>
          
          {/* LEFT PANEL */}
          <div className={`w-full md:w-1/3 p-12 flex flex-col items-center justify-center transition-colors ${panelGradient} ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>
            <div className={`w-24 h-24 rounded-[2rem] flex items-center justify-center mb-6 border shadow-xl backdrop-blur-md transition-all ${isDark ? 'bg-white/5 border-white/10' : 'bg-white/40 border-white/40'}`}>
              {isCreateMode ? <UserPlus size={40} className="text-indigo-400" /> : <User size={40} className="text-indigo-400" />}
            </div>
            <div className="text-center">
              <h2 className={`text-2xl font-bold uppercase tracking-tight ${isDark ? 'text-white' : 'text-indigo-900'}`}>
                {isCreateMode ? "New Member" : profile.full_name || "Staff Profile"}
              </h2>
              <p className={`text-xs font-semibold uppercase tracking-[0.2em] mt-2 leading-relaxed ${isDark ? 'text-slate-400' : 'text-indigo-700/60'}`}>
                {isCreateMode ? "System Access" : profile.role}
              </p>
            </div>
          </div>

          {/* RIGHT PANEL (FORM) */}
          <div className={`w-full md:w-2/3 p-8 lg:p-12 transition-colors ${isDark ? 'bg-slate-900' : 'bg-white'}`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
              <DetailInput 
                isDark={isDark} 
                label="Full Name" 
                icon={User} 
                value={profile.full_name} 
                disabled={!isEditing} 
                onChange={(e) => setProfile({...profile, full_name: e.target.value})} 
                secondaryTextClass={secondaryTextColor}
              />
              <DetailInput 
                isDark={isDark} 
                label="Email Address" 
                icon={Mail} 
                value={profile.email} 
                disabled={!isEditing} 
                onChange={(e) => setProfile({...profile, email: e.target.value})} 
                secondaryTextClass={secondaryTextColor}
              />
              <DropdownInput 
                isDark={isDark} 
                label="System Role" 
                icon={ShieldCheck} 
                value={profile.role} 
                options={roles} 
                disabled={!isEditing} 
                onChange={(v) => setProfile({...profile, role: v})} 
                secondaryTextClass={secondaryTextColor}
              />
              
              <div className="relative">
                <DetailInput 
                  isDark={isDark} 
                  label={isCreateMode ? "Set Password" : "Change Password"} 
                  icon={Lock} 
                  type={showPassword ? "text" : "password"}
                  value={profile.password} 
                  disabled={!isEditing} 
                  placeholder={isEditing ? (isCreateMode ? "Required" : "Leave blank to keep same") : "••••••••"}
                  onChange={(e) => setProfile({...profile, password: e.target.value})} 
                  secondaryTextClass={secondaryTextColor}
                />
                {isEditing && (
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 bottom-4 text-slate-400 hover:text-indigo-500">
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                )}
              </div>
            </div>

            <div className="mt-12 flex items-center gap-3">
              {isEditing && (
                <>
                  <Button 
                    isDark={isDark}
                    onClick={handleSave} 
                    disabled={loading}
                    className={`flex-grow h-[58px] rounded-2xl font-bold shadow-xl uppercase tracking-widest flex items-center justify-center gap-2 border transition-transform active:scale-95 ${panelGradient} ${
                      isDark ? 'border-slate-700 text-slate-300' : 'border-indigo-200 text-slate-600'
                    }`}
                  >
                    {loading ? <><Loader2 className="animate-spin" size={18} /> Processing...</> : (isCreateMode ? "Create Member" : "Update Profile")}
                  </Button>

                  {!isCreateMode && (
                    <button 
                      type="button"
                      onClick={handleDeleteUser}
                      className={`w-[60px] h-[58px] rounded-2xl border flex items-center justify-center transition-all active:scale-95 shadow-xl ${
                        isDark 
                        ? 'bg-slate-800 border-slate-700 text-orange-500 hover:bg-rose-500/10' 
                        : 'bg-white border-white-200 text-orange-600 hover:bg-rose-50'
                      }`}
                      title="Purge User"
                    >
                      <Trash2 size={22} />
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DetailInput({ label, icon: Icon, isDark, disabled, secondaryTextClass, ...props }) {
  return (
    <div className="text-left w-full">
      <label className={`text-xs font-bold uppercase mb-2 ml-1 block tracking-widest ${secondaryTextClass}`}>
        {label}
      </label>
      <div className="relative">
        <Icon size={14} className={`absolute left-4 top-1/2 -translate-y-1/2 ${disabled ? 'text-slate-500' : 'text-indigo-500'}`} />
        <input 
          {...props} 
          disabled={disabled}
          className={`w-full rounded-xl pl-11 pr-4 py-4 text-sm font-semibold transition-all outline-none border ${
            isDark 
            ? 'bg-slate-800 border-slate-700 text-white focus:bg-slate-950 focus:border-indigo-900' 
            : 'bg-slate-50 border-slate-200 text-slate-900 focus:bg-white focus:border-indigo-100'
          } ${disabled ? 'opacity-50 cursor-not-allowed grayscale' : 'opacity-100'}`} 
        />
      </div>
    </div>
  );
}

function DropdownInput({ label, icon: Icon, options, value, onChange, disabled, isDark, secondaryTextClass }) {
  return (
    <div className="text-left w-full">
      <label className={`text-xs font-bold uppercase mb-2 ml-1 block tracking-widest ${secondaryTextClass}`}>
        {label}
      </label>
      <div className="relative">
        <Icon size={14} className={`absolute left-4 top-1/2 -translate-y-1/2 z-10 ${disabled ? 'text-slate-500' : 'text-indigo-500'}`} />
        <select 
          disabled={disabled} 
          value={value} 
          onChange={(e) => onChange(e.target.value)} 
          className={`w-full rounded-xl pl-11 pr-4 py-4 text-sm font-semibold appearance-none outline-none transition-all border ${
            isDark 
            ? 'bg-slate-800 border-slate-700 text-white focus:bg-slate-950 focus:border-indigo-900' 
            : 'bg-slate-50 border-slate-200 text-slate-900 focus:bg-white focus:border-indigo-100'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : 'opacity-100'}`}
        >
          {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        </select>
      </div>
    </div>
  );
}